import type { PhotoCustomizationArea } from "../types/products";

export const DEFAULT_PENDANT_ENGRAVING_SIZE_PX = 32;
export const DEFAULT_PENDANT_PREVIEW_REFERENCE_SIZE_PX = 520;
export const PENDANT_PHOTO_BLEED_PCT = 8;
export const PENDANT_TEXT_LINE_HEIGHT = 1.15;
export const PENDANT_TEXT_ALIGN = "center";

type AreaBounds = {
  xPct: number;
  yPct: number;
  widthPct: number;
  heightPct: number;
};

export type ResolvedPendantArea = {
  enabled: boolean;
  shape: "circle" | "rounded-rect" | "heart";
  coordinateSpace: "container" | "image";
  xPct: number;
  yPct: number;
  widthPct: number;
  heightPct: number;
  centerXPct?: number;
  centerYPct?: number;
  radiusPct?: number;
  cornerRadiusPct: number;
  heartTopWidthPct: number;
  heartNotchDepthPct: number;
  heartNotchWidthPct: number;
};

export type ContainedImageBounds = AreaBounds & {
  x: number;
  y: number;
  width: number;
  height: number;
};

const clamp = (n: number, min: number, max: number) =>
  Math.min(max, Math.max(min, n));

const isFiniteNumber = (value: unknown): value is number =>
  Number.isFinite(Number(value));

const CIRCLE_MATCH_TOLERANCE_PCT = 0.5;

export function resolvePendantArea(
  area?: PhotoCustomizationArea
): ResolvedPendantArea {
  const shape =
    String(area?.shape || "").toLowerCase() === "rounded-rect"
      ? "rounded-rect"
      : String(area?.shape || "").toLowerCase() === "heart"
      ? "heart"
      : "circle";
  const coordinateSpace =
    String(area?.coordinateSpace || "").toLowerCase() === "image"
      ? "image"
      : "container";
  const rawWidthPct = clamp(Number(area?.widthPct ?? 40), 5, 95);
  const rawHeightPct = clamp(Number(area?.heightPct ?? 40), 5, 95);
  let widthPct = rawWidthPct;
  let heightPct = rawHeightPct;
  let xPct = clamp(Number(area?.xPct ?? 30), 0, 100 - widthPct);
  let yPct = clamp(Number(area?.yPct ?? 30), 0, 100 - heightPct);
  const centerXPctRaw = Number(area?.centerXPct);
  const centerYPctRaw = Number(area?.centerYPct);
  const radiusPctRaw = Number(area?.radiusPct);
  const hasExplicitCircleMetrics =
    shape === "circle" &&
    isFiniteNumber(centerXPctRaw) &&
    centerXPctRaw >= 0 &&
    centerXPctRaw <= 100 &&
    isFiniteNumber(centerYPctRaw) &&
    centerYPctRaw >= 0 &&
    centerYPctRaw <= 100 &&
    isFiniteNumber(radiusPctRaw) &&
    radiusPctRaw > 0 &&
    radiusPctRaw <= 50;
  const shouldNormalizeCircleBounds =
    shape === "circle" &&
    Math.abs(widthPct - heightPct) <= CIRCLE_MATCH_TOLERANCE_PCT;

  if (shouldNormalizeCircleBounds) {
    const centerXPct = xPct + widthPct / 2;
    const centerYPct = yPct + heightPct / 2;
    const diameterPct = clamp((widthPct + heightPct) / 2, 5, 95);
    widthPct = diameterPct;
    heightPct = diameterPct;
    xPct = clamp(centerXPct - diameterPct / 2, 0, 100 - diameterPct);
    yPct = clamp(centerYPct - diameterPct / 2, 0, 100 - diameterPct);
  }

  return {
    enabled: area?.enabled ?? true,
    shape,
    coordinateSpace,
    xPct,
    yPct,
    widthPct,
    heightPct,
    centerXPct:
      shape === "circle"
        ? hasExplicitCircleMetrics
          ? clamp(centerXPctRaw, 0, 100)
          : xPct + widthPct / 2
        : isFiniteNumber(area?.centerXPct) && Number(area?.centerXPct) >= 0
        ? Number(area?.centerXPct)
        : undefined,
    centerYPct:
      shape === "circle"
        ? hasExplicitCircleMetrics
          ? clamp(centerYPctRaw, 0, 100)
          : yPct + heightPct / 2
        : isFiniteNumber(area?.centerYPct) && Number(area?.centerYPct) >= 0
        ? Number(area?.centerYPct)
        : undefined,
    radiusPct:
      shape === "circle"
        ? hasExplicitCircleMetrics
          ? clamp(radiusPctRaw, 2.5, 50)
          : shouldNormalizeCircleBounds
          ? widthPct / 2
          : undefined
        : isFiniteNumber(area?.radiusPct) && Number(area?.radiusPct) > 0
        ? Number(area?.radiusPct)
        : undefined,
    cornerRadiusPct: clamp(Number(area?.cornerRadiusPct ?? 16), 0, 50),
    heartTopWidthPct: clamp(Number(area?.heartTopWidthPct ?? 34), 18, 70),
    heartNotchDepthPct: clamp(Number(area?.heartNotchDepthPct ?? 28), 8, 55),
    heartNotchWidthPct: clamp(Number(area?.heartNotchWidthPct ?? 8), 2, 24),
  };
}

export function getContainedImageBounds(
  containerWidth: number,
  containerHeight: number,
  imageWidth?: number | null,
  imageHeight?: number | null
): ContainedImageBounds | null {
  const safeContainerWidth = Number(containerWidth);
  const safeContainerHeight = Number(containerHeight);
  const safeImageWidth = Number(imageWidth);
  const safeImageHeight = Number(imageHeight);
  if (
    !Number.isFinite(safeContainerWidth) ||
    !Number.isFinite(safeContainerHeight) ||
    safeContainerWidth <= 0 ||
    safeContainerHeight <= 0 ||
    !Number.isFinite(safeImageWidth) ||
    !Number.isFinite(safeImageHeight) ||
    safeImageWidth <= 0 ||
    safeImageHeight <= 0
  ) {
    return null;
  }

  const scale = Math.min(
    safeContainerWidth / safeImageWidth,
    safeContainerHeight / safeImageHeight
  );
  const width = safeImageWidth * scale;
  const height = safeImageHeight * scale;
  const x = (safeContainerWidth - width) / 2;
  const y = (safeContainerHeight - height) / 2;

  return {
    x,
    y,
    width,
    height,
    xPct: (x / safeContainerWidth) * 100,
    yPct: (y / safeContainerHeight) * 100,
    widthPct: (width / safeContainerWidth) * 100,
    heightPct: (height / safeContainerHeight) * 100,
  };
}

export function resolvePendantDisplayArea(
  area?: PhotoCustomizationArea,
  imageBounds?: AreaBounds | null
): ResolvedPendantArea {
  const resolved = resolvePendantArea(area);
  if (resolved.coordinateSpace !== "image" || !imageBounds) {
    return resolved;
  }

  if (
    resolved.shape === "circle" &&
    isFiniteNumber(resolved.centerXPct) &&
    isFiniteNumber(resolved.centerYPct) &&
    isFiniteNumber(resolved.radiusPct)
  ) {
    const centerX =
      imageBounds.xPct + (Number(resolved.centerXPct) / 100) * imageBounds.widthPct;
    const centerY =
      imageBounds.yPct + (Number(resolved.centerYPct) / 100) * imageBounds.heightPct;
    const diameterPct =
      ((Number(resolved.radiusPct) * 2) / 100) *
      Math.min(imageBounds.widthPct, imageBounds.heightPct);
    return {
      ...resolved,
      xPct: clamp(centerX - diameterPct / 2, 0, 100 - diameterPct),
      yPct: clamp(centerY - diameterPct / 2, 0, 100 - diameterPct),
      widthPct: diameterPct,
      heightPct: diameterPct,
    };
  }

  return {
    ...resolved,
    xPct: imageBounds.xPct + (resolved.xPct / 100) * imageBounds.widthPct,
    yPct: imageBounds.yPct + (resolved.yPct / 100) * imageBounds.heightPct,
    widthPct: (resolved.widthPct / 100) * imageBounds.widthPct,
    heightPct: (resolved.heightPct / 100) * imageBounds.heightPct,
  };
}

export function projectDisplayAreaToImageSpace(
  area: AreaBounds & { shape: "circle" | "heart" },
  imageBounds?: AreaBounds | null,
  options?: { preserveCircleMetrics?: boolean }
): Pick<
  ResolvedPendantArea,
  | "coordinateSpace"
  | "shape"
  | "xPct"
  | "yPct"
  | "widthPct"
  | "heightPct"
  | "centerXPct"
  | "centerYPct"
  | "radiusPct"
> {
  if (!imageBounds) {
    return {
      coordinateSpace: "container",
      shape: area.shape,
      xPct: area.xPct,
      yPct: area.yPct,
      widthPct: area.widthPct,
      heightPct: area.heightPct,
    };
  }

  const centerXPct =
    ((area.xPct + area.widthPct / 2 - imageBounds.xPct) / imageBounds.widthPct) *
    100;
  const centerYPct =
    ((area.yPct + area.heightPct / 2 - imageBounds.yPct) / imageBounds.heightPct) *
    100;
  const xPct = clamp(
    ((area.xPct - imageBounds.xPct) / imageBounds.widthPct) * 100,
    0,
    100
  );
  const yPct = clamp(
    ((area.yPct - imageBounds.yPct) / imageBounds.heightPct) * 100,
    0,
    100
  );
  const widthPct = clamp((area.widthPct / imageBounds.widthPct) * 100, 5, 95);
  const heightPct = clamp((area.heightPct / imageBounds.heightPct) * 100, 5, 95);

  if (area.shape === "circle") {
    const preserveCircleMetrics = options?.preserveCircleMetrics !== false;
    const radiusPct =
      (Math.max(area.widthPct, area.heightPct) / 2 / Math.min(
        imageBounds.widthPct,
        imageBounds.heightPct
      )) *
      100;
    return preserveCircleMetrics
      ? {
          coordinateSpace: "image",
          shape: "circle",
          xPct,
          yPct,
          widthPct,
          heightPct,
          centerXPct: clamp(centerXPct, 0, 100),
          centerYPct: clamp(centerYPct, 0, 100),
          radiusPct: clamp(radiusPct, 2.5, 50),
        }
      : {
          coordinateSpace: "image",
          shape: "circle",
          xPct,
          yPct,
          widthPct,
          heightPct,
        };
  }

  return {
    coordinateSpace: "image",
    shape: "heart",
    xPct,
    yPct,
    widthPct,
    heightPct,
  };
}

export function normalizePreviewReferenceSizePx(
  value: unknown,
  fallback = DEFAULT_PENDANT_PREVIEW_REFERENCE_SIZE_PX
) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback;
  return parsed;
}

export function getEngravingPreviewScale(
  previewSizePx: number,
  referenceSizePx?: unknown
) {
  return (
    Math.max(1, Number(previewSizePx) || 0) /
    normalizePreviewReferenceSizePx(referenceSizePx)
  );
}

export function getScaledEngravingFontSize(
  sizePx: unknown,
  previewSizePx: number,
  referenceSizePx?: unknown
) {
  const base = Math.max(
    8,
    Number(sizePx ?? DEFAULT_PENDANT_ENGRAVING_SIZE_PX) ||
      DEFAULT_PENDANT_ENGRAVING_SIZE_PX
  );
  return base * getEngravingPreviewScale(previewSizePx, referenceSizePx);
}

export function getScaledEngravingLetterSpacing(
  letterSpacingPx: unknown,
  previewSizePx: number,
  referenceSizePx?: unknown
) {
  return (
    Number(letterSpacingPx || 0) *
    getEngravingPreviewScale(previewSizePx, referenceSizePx)
  );
}
